import { useCallback } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";

import { useAlert } from "capitalrx-components-2.0/lib/general";
import { useModulePathResolver } from "capitalrx-shell";

import { GeneralTypes } from "@/types";

interface HandlerOptions {
  callback?: Function;
  invalidates: Array<string>;
  message?: string;
  redirect?: string;
  redirectToEdit?: boolean;
}

/**
 *
 * @param callback Optional: Callback for instances where you need to perform some other task.
 * @param invalidates React Query key to invalidate query and trigger refetch.
 * @param message Optional: Can pass a custom toast message to be displayed.
 * @param redirect Optional: Path to redirect user to on successful operation.
 * @returns successHandler
 */
export function useSuccessHandler(options: HandlerOptions): (data: unknown) => void {
  const showAlert = useAlert();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const modulePath = useModulePathResolver();

  return useCallback(
    (data: GeneralTypes.ReturnTypePartial): void => {
      const { message = "Success!", invalidates, redirect, callback, redirectToEdit } = options;

      if (invalidates !== null) {
        queryClient.invalidateQueries({ queryKey: invalidates });
      }

      // if there is a callback, call it
      if (typeof callback == "function") callback(data);

      if (redirect !== undefined && redirect !== null) {
        if (data !== undefined && redirectToEdit) {
          navigate(modulePath(`${redirect}/${data.id}`));
        } else {
          navigate(modulePath(redirect));
        }
      }

      showAlert({
        message,
        type: "success",
        style: "banner",
      });
    },
    [history, queryClient, modulePath, showAlert],
  );
}
